import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Layout from 'Component/Layouts/layout';
import useApiCaller from 'hooks/apiCaller';
import CreatePolicyForm from './CreatePolicyForm';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useCredentials from 'hooks/useCredentials';
import DeleteIcon from '@mui/icons-material/Delete';

const SFLPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const apiCaller = useApiCaller();
  const { userData: user } = useCredentials();
  const isSuperUser = user?.learningRoles?.includes('admin');

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await apiCaller.get('/user/get-all-policies');
      if (response?.data?.data?.policies) {
        setPolicies(response.data.data.policies);
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
      toast.error('Failed to fetch policies');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePolicyCreated = () => {
    fetchPolicies();
    setTabValue(0);
  };

  const handleDeleteClick = (policy) => {
    setPolicyToDelete(policy);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiCaller.delete(`/super-user/delete-policy/${policyToDelete._id}`);
      toast.success('Policy deleted successfully');
      fetchPolicies();
    } catch (error) {
      console.error('Error deleting policy:', error);
      toast.error('Failed to delete policy');
    }
    setDeleteDialogOpen(false);
    setPolicyToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPolicyToDelete(null);
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            fontSize: '24px',
            fontWeight: 500,
            background: 'linear-gradient(to bottom, #E9FBE0 0%, #E6F0E8 100%)',
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px',
          }}
        >
          SFL Training Policies
        </Box>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            mb: 3,
            mt: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          <Tab label="View Policies" />
          {isSuperUser && <Tab label="Upload New Policy" />}
        </Tabs>

        {tabValue === 0 ? (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderRadius: '8px',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Policy Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Upload Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {policies.map((policy) => (
                  <TableRow
                    key={policy._id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f9f9f9',
                      },
                    }}
                  >
                    <TableCell>{policy.policyName}</TableCell>
                    <TableCell>{new Date(policy.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          href={policy.policyDocumentUrl}
                          target="_blank"
                          sx={{
                            borderColor: '#1976d2',
                            color: '#1976d2',
                            '&:hover': {
                              borderColor: '#1565c0',
                              backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            },
                          }}
                        >
                          View
                        </Button>
                        {isSuperUser && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleDeleteClick(policy)}
                            sx={{
                              borderColor: '#d32f2f',
                              color: '#d32f2f',
                              '&:hover': {
                                borderColor: '#b71c1c',
                                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Delete
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <CreatePolicyForm onPolicyCreated={handlePolicyCreated} />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the policy "{policyToDelete?.policyName}"? This action
              cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default SFLPolicies;
